import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import styles from '../../SearchProvider.module.scss'
import InputLabel from 'components/SA/FormElements/InputLabel'
import { getHrefFunction, updateClientSideUrl } from '../../helpers/url'
import { convertLocaleToDefaultCurrency, getCurrencySymbol } from 'components/FormatPrice/helpers'
import { LocaleCurrency } from 'types/Shared/Locale'
import { SASelectedFacets } from 'types/SearchProvider'
import { CustomHandleUpdateSelectedFacetParams, SharedFacetNames } from '../../types'

export type PriceObject = {
  priceMax: number | null
  priceMin: number | null
  priceError: boolean
}

const initialPriceState: PriceObject = {
  priceMax: null,
  priceMin: null,
  priceError: false,
}

type CustomPriceFieldsProps = {
  facetName: string
  baseUrl: string
  nextAppRoute: string
  priceMinMax: {
    priceMin: number | null
    priceMax: number | null
  }
  selectedCurrency: LocaleCurrency
  selectedFacets: SASelectedFacets
  defaultPriceValues: ReadonlyArray<string>
  basicQueryParams: Record<string, ReadonlyArray<string>>
  customHandleUpdateSelectedFacet?: (params: CustomHandleUpdateSelectedFacetParams) => void
  customClickFacetOption?: (params: CustomHandleUpdateSelectedFacetParams) => void
  pathParams: ReadonlyArray<string>
  sharedFacetNames: SharedFacetNames
}

export type CustomPriceFieldsApi = {
  resetCustomPriceFields: () => void
}

const CustomPriceFields = forwardRef<CustomPriceFieldsApi, CustomPriceFieldsProps>(
  (
    {
      facetName,
      baseUrl,
      nextAppRoute,
      priceMinMax,
      selectedCurrency,
      selectedFacets,
      defaultPriceValues,
      basicQueryParams,
      customHandleUpdateSelectedFacet,
      customClickFacetOption,
      pathParams,
      sharedFacetNames,
    },
    ref
  ) => {
    let timer
    const timerDuration = 1500

    const priceMinProp = priceMinMax?.priceMin
    const priceMaxProp = priceMinMax?.priceMax
    const initialCustomPriceOpenState =
      !!priceMinProp &&
      !!priceMaxProp &&
      !defaultPriceValues.includes(`${priceMinProp}-${priceMaxProp}`)
    const [isCustomPriceOpen, setIsCustomPriceOpen] = useState(initialCustomPriceOpenState)
    const [priceState, setPriceState] = useState<PriceObject>({
      ...initialPriceState,
      priceMin: !!priceMinProp ? priceMinProp : null,
      priceMax: !!priceMaxProp ? priceMaxProp : null,
    })
    const { priceMin, priceMax, priceError } = priceState

    const handleCustomPriceToggle = () => {
      const { priceMin, priceMax } = priceState
      const shouldResetValue =
        (priceMin !== null && priceMin >= 0) || (priceMax !== null && priceMax > 0)
      if (isCustomPriceOpen && shouldResetValue) {
        setPriceState(initialPriceState)
        const newSelectedFacets = {
          ...selectedFacets,
          [facetName]: [],
          [`custom__${facetName}`]: [],
          ...basicQueryParams,
          page: [''],
        }
        const href = getHrefFunction({
          facetsAndQueryParams: newSelectedFacets,
          pathParams,
          sharedFacetNames,
        })()
        const newUrl = `${baseUrl}${href}`

        if (Boolean(customHandleUpdateSelectedFacet)) {
          customHandleUpdateSelectedFacet?.({
            newSelectedFacets,
            newUrl,
            facetName,
            facetValue: '',
          })
          return
        }

        customClickFacetOption?.({
          newSelectedFacets,
          newUrl,
          facetName,
          facetValue: '',
        })
        updateClientSideUrl(newUrl, nextAppRoute)
      }

      if (!isCustomPriceOpen && shouldResetValue) {
        setPriceState(initialPriceState)
      }
      setIsCustomPriceOpen((prevValue) => !prevValue)
    }

    const validateNumber = (value: string): number => {
      const validNumber = Number(value)
      return isNaN(validNumber) || validNumber < 0 ? 0 : validNumber
    }

    const validatePrice = ({
      priceMin,
      priceMax,
    }: {
      priceMin: number | null
      priceMax: number | null
    }): boolean => {
      if (priceMin === null || priceMax === null) {
        return false
      }

      return priceMin >= 0 && priceMax > 0 && priceMin <= priceMax
    }

    const inputUpdate = (data) => {
      clearTimeout(timer)
      const newPriceState = { ...priceState, ...data }
      const error = !validatePrice(newPriceState)
      console.log('newPriceState', newPriceState)
      const customPriceError =
        error &&
        Boolean(newPriceState?.priceMin) &&
        newPriceState?.priceMin >= 0 &&
        newPriceState?.priceMax > 0
      setPriceState((prevValues) => ({ ...prevValues, ...data, priceError: customPriceError }))

      if (!error) {
        timer = setTimeout(() => {
          if (window && window.dataLayer) {
            window.dataLayer.push({
              event: 'filter',
              label: `Price - Custom - ${newPriceState.priceMin || ''}-${newPriceState.priceMax ||
                ''}`,
            })
          }

          const convertedMinPrice = convertLocaleToDefaultCurrency(
            newPriceState.priceMin,
            selectedCurrency
          )
          const convertedMaxPrice = convertLocaleToDefaultCurrency(
            newPriceState.priceMax,
            selectedCurrency
          )
          const convertedPriceFacetValue = `${convertedMinPrice}-${convertedMaxPrice}`
          const priceFacetValue = `${newPriceState.priceMin}-${newPriceState.priceMax}`
          const newSelectedFacets = {
            ...selectedFacets,
            [facetName]: [convertedPriceFacetValue],
            [`custom__${facetName}`]: [priceFacetValue],
            ...basicQueryParams,
            page: [''],
          }
          const href = getHrefFunction({
            facetsAndQueryParams: newSelectedFacets,
            pathParams,
            sharedFacetNames,
          })()
          const newUrl = `${baseUrl}${href}`

          if (Boolean(customHandleUpdateSelectedFacet)) {
            customHandleUpdateSelectedFacet?.({
              newSelectedFacets,
              newUrl,
              facetName,
              facetValue: convertedPriceFacetValue,
            })
            return
          }

          customClickFacetOption?.({
            newSelectedFacets,
            newUrl,
            facetName,
            facetValue: convertedPriceFacetValue,
          })
          updateClientSideUrl(newUrl, nextAppRoute)
        }, timerDuration)
      }

      return timer
    }

    const resetCustomPriceFields = () => {
      setIsCustomPriceOpen(false)
      setPriceState(initialPriceState)
    }

    useImperativeHandle(ref, () => ({
      resetCustomPriceFields,
    }))

    useEffect(() => {
      const priceMinProp = priceMinMax?.priceMin
      const priceMaxProp = priceMinMax?.priceMax
      if (priceMinProp === null && priceMaxProp === null) {
        setPriceState(initialPriceState)
        setIsCustomPriceOpen(false)
      }
    }, [priceMinMax])

    return (
      <div className={styles.customSizeContainer}>
        <button onClick={handleCustomPriceToggle}>
          {isCustomPriceOpen ? 'Remove Custom Price' : 'Select Custom Price'}
        </button>
        {isCustomPriceOpen && (
          <div className={styles.sizeInputWrapper}>
            <div className={styles.sizeInputContainer}>
              <InputLabel
                type='text'
                name='priceMin'
                title={`${getCurrencySymbol(selectedCurrency)} Min`}
                value={priceMin || priceMin === 0 ? priceMin : ''}
                onChange={(ev) =>
                  inputUpdate({
                    priceMin: validateNumber(ev.target.value),
                  })
                }
                error={priceError ? ' ' : undefined}
              />
              {'-'}
              <InputLabel
                type='text'
                name='priceMax'
                title={`${getCurrencySymbol(selectedCurrency)} Max`}
                value={priceMax || ''}
                onChange={(ev) =>
                  inputUpdate({
                    priceMax: validateNumber(ev.target.value),
                  })
                }
                error={priceError ? ' ' : undefined}
              />
            </div>
          </div>
        )}
      </div>
    )
  }
)

export default CustomPriceFields
